import STATE_NAMES from '@/common/store/glb/data.state.js';
import { joinLightspeedUrl, LIGHTSPEED_PATHS } from '@/common/utilities/lightspeed-url.js';

const RESIDENCE_TYPE_BY_INDEX = {
    1: 'primaryResidence',
    2: 'secondaryResidence',
    3: 'investment',
};

const PROPERTY_TYPE_MAP = {
    SINGLE_FAMILY_HOUSE: 'singleFamily',
    CONDO: 'condo',
    MULTI_FAMILY: 'multiFamily',
    MOBILE: 'manufactured',
};

const LIGHTSPEED_DC = 'District Of Columbia';
const MONEY_MAX = 1_000_000_000_000;
const INCOME_MAX = 1_000_000;

function setIf(target, key, value) {
    if (value === undefined || value === null || value === '') {
        return;
    }
    target[key] = value;
}

function clipOrOmit(value, max) {
    const text = String(value || '').trim();
    if (!text || text.length > max) {
        return '';
    }
    return text;
}

function emailLocalPart(email) {
    const text = String(email || '').trim();
    const at = text.indexOf('@');
    return at > 0 ? text.slice(0, at) : '';
}

function mapIdentity(user = {}) {
    const fields = {};
    const email = String(user.email || '').trim();
    if (email.length <= 80 && /.+@.+\..+/.test(email)) {
        fields.borrowerEmail = email;
    }

    const phone = String(user.phone || '').trim();
    if (phone) {
        fields.borrowerPhoneNumber = phone;
    }

    const fullName = String(user.fullName || '').trim();
    const placeholder = emailLocalPart(email);
    if (!fullName || (placeholder && fullName === placeholder)) {
        return fields;
    }

    const parts = fullName.split(/\s+/).filter(Boolean);
    setIf(fields, 'borrowerFirstName', clipOrOmit(parts[0], 25));
    setIf(fields, 'borrowerLastName', clipOrOmit(parts.slice(1).join(' '), 80));
    return fields;
}

function mapResidenceType(index) {
    return RESIDENCE_TYPE_BY_INDEX[Number(index)] || '';
}

function mapPropertyType(propertyType) {
    return PROPERTY_TYPE_MAP[propertyType] || '';
}

function mapState(raw) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) {
        return '';
    }
    if (/^dc$/i.test(trimmed) || /^washington\s+d\.?c\.?$/i.test(trimmed)) {
        return LIGHTSPEED_DC;
    }

    const fromCode = STATE_NAMES[trimmed.toUpperCase()];
    if (fromCode) {
        return fromCode === 'Washington DC' ? LIGHTSPEED_DC : fromCode;
    }

    const known = Object.values(STATE_NAMES).find((name) => name.toLowerCase() === trimmed.toLowerCase());
    if (known) {
        return known === 'Washington DC' ? LIGHTSPEED_DC : known;
    }

    if (trimmed.toLowerCase() === LIGHTSPEED_DC.toLowerCase()) {
        return LIGHTSPEED_DC;
    }

    return '';
}

function mapZip(raw) {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 5);
    return digits.length === 5 ? digits : '';
}

function resolveGeo(claim = {}) {
    const propertyGeo = claim.property?.geo || {};
    const dppGeo = claim.dpp?.geo || {};
    return {
        address: propertyGeo.address || dppGeo.address || '',
        city: propertyGeo.city || dppGeo.city || '',
        state: propertyGeo.state || dppGeo.state || '',
        zipcode: propertyGeo.zipcode || propertyGeo.zipCode || propertyGeo.zip || dppGeo.zipcode || dppGeo.zipCode || dppGeo.zip || '',
    };
}

function mapAddress(geo, prefix) {
    const fields = {};
    setIf(fields, `${prefix}StreetAddress`, clipOrOmit(geo.address, 100));
    setIf(fields, `${prefix}City`, clipOrOmit(geo.city, 40));
    setIf(fields, `${prefix}State`, mapState(geo.state));
    setIf(fields, `${prefix}ZipCode`, mapZip(geo.zipcode));
    return fields;
}

function mapMoney(value, max = MONEY_MAX) {
    const amount = Math.round(Number(value));
    if (!Number.isFinite(amount) || amount < 1 || amount > max) {
        return undefined;
    }
    return amount;
}

export function buildLightspeedPrefillFields({
    loanType,
    user = {},
    claim = {},
    dashboard = {},
    financialProfile = {},
    purchaseEstimate = {},
} = {}) {
    const fields = {
        ...mapIdentity(user),
    };

    const property = claim.property || {};
    setIf(fields, 'propertyResidenceType', mapResidenceType(property.propertyPurposeIndex));
    setIf(fields, 'propertyType', mapPropertyType(property.propertyType || claim.dpp?.propertyType));

    const geo = resolveGeo(claim);
    const isPurchase = loanType === 'purchase';
    const residenceType = fields.propertyResidenceType;

    if (isPurchase) {
        setIf(fields, 'propertyState', mapState(geo.state));
        Object.assign(fields, mapAddress(geo, 'borrower'));
        setIf(fields, 'propertyPurchasePrice', mapMoney(purchaseEstimate.maxPrice));
        setIf(fields, 'propertyDownPayment', mapMoney(dashboard.equity));
    } else {
        Object.assign(fields, mapAddress(geo, 'property'));
        setIf(fields, 'propertyHomeValue', mapMoney(dashboard.homeValue));
        setIf(fields, 'propertyMortgageBalance', mapMoney(dashboard.unpaidBalance));
        if (residenceType === 'primaryResidence') {
            Object.assign(fields, mapAddress(geo, 'borrower'));
        }
    }

    setIf(fields, 'combinedHouseholdIncome', mapMoney(financialProfile.householdIncome, INCOME_MAX));
    return fields;
}

function safeDecodeUri(value) {
    const text = String(value ?? '');
    if (!text) {
        return '';
    }
    try {
        return decodeURIComponent(text);
    } catch (e) {
        return text;
    }
}

/**
 * Build a claim-shaped object from DPP/listing CTA context so non-HOD uses the same
 * HOME-65 field mapper as HOD (shopping state + borrower/current address from geo).
 */
export function claimFromListing(listing = {}) {
    const geo = listing.geo || {};
    return {
        property: {
            propertyType: listing.propertyType || geo.propertyType || '',
            // DPP listings have no occupancy purpose; treat as primary so refinance also
            // copies borrower address the same way HOD does for primary homes.
            propertyPurposeIndex: listing.propertyPurposeIndex || 1,
            geo: {
                address: safeDecodeUri(listing.address || geo.address),
                city: safeDecodeUri(listing.city || geo.city),
                state: safeDecodeUri(listing.state || geo.state),
                zipcode: listing.zipcode || listing.zipCode || listing.zip || geo.zipcode || geo.zipCode || geo.zip || '',
            },
        },
    };
}

/**
 * Non-HOD Lightspeed POST fields — same mapper as HOD, with listing standing in for claim.
 */
export function buildNonHodLightspeedPrefillFields({
    loanType,
    user = {},
    financialProfile = {},
    listing = {},
} = {}) {
    const claim = claimFromListing(listing);
    const price = listing.price ?? listing.listPrice ?? listing.list_price;
    const isPurchase = loanType === 'purchase';

    return buildLightspeedPrefillFields({
        loanType,
        user,
        claim,
        financialProfile,
        purchaseEstimate: isPurchase ? { maxPrice: price } : {},
        dashboard: !isPurchase ? { homeValue: price } : {},
    });
}

export function buildLightspeedPrefillUrl({
    lightspeedUrl,
    loanType,
    userId,
    utmContent,
    pathname,
    property = {},
} = {}) {
    const host = joinLightspeedUrl(lightspeedUrl, LIGHTSPEED_PATHS.prefill);
    const redirectPath = pathname || (typeof location !== 'undefined' ? location.pathname : '/');
    let url =
        `${host}?partner_name=movoto` +
        `&rea_question=true` +
        `&consent_required=true&show_warm_up=false` +
        `&redirect_url=${encodeURIComponent('https://www.movoto.com' + redirectPath)}`;

    if (loanType) {
        url += `&loan_type=${encodeURIComponent(loanType)}`;
    }
    if (userId) {
        url += `&vendor_lead_unique_id=${encodeURIComponent(userId)}`;
    }
    if (utmContent) {
        url += `&utm_content=${utmContent}`;
    }
    if (property.propertyId) {
        url += `&utm_medium=${encodeURIComponent(property.propertyId)}`;
    }

    return url;
}

export const LIGHTSPEED_HANDOFF_URL_KEY = 'lowerUrl';
export const LIGHTSPEED_HANDOFF_FIELDS_KEY = 'lowerPrefillFields';

/** Persist Lightspeed POST handoff across the confirm-mortgage-lower interstitial. */
export function persistLightspeedHandoff({ url, fields } = {}) {
    if (typeof localStorage === 'undefined') {
        return;
    }
    if (url) {
        localStorage.setItem(LIGHTSPEED_HANDOFF_URL_KEY, url);
    }
    localStorage.setItem(LIGHTSPEED_HANDOFF_FIELDS_KEY, JSON.stringify(fields || {}));
}

/** Read and clear the interstitial handoff (URL + POST fields). */
export function consumeLightspeedHandoff() {
    if (typeof localStorage === 'undefined') {
        return { url: '', fields: {} };
    }

    const url = localStorage.getItem(LIGHTSPEED_HANDOFF_URL_KEY) || '';
    let fields = {};
    try {
        const raw = localStorage.getItem(LIGHTSPEED_HANDOFF_FIELDS_KEY);
        fields = raw ? JSON.parse(raw) || {} : {};
    } catch (e) {
        fields = {};
    }

    localStorage.removeItem(LIGHTSPEED_HANDOFF_URL_KEY);
    localStorage.removeItem(LIGHTSPEED_HANDOFF_FIELDS_KEY);
    return { url, fields };
}

export function submitLightspeedPrefill({ url, fields } = {}) {
    if (typeof document === 'undefined') {
        return;
    }

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.target = '_blank';
    form.rel = 'noopener';
    form.style.display = 'none';

    Object.entries(fields || {}).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = String(value);
        form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    form.remove();
}

export function openHomeownerLightspeedPrefill(opts = {}) {
    const property = opts.claim?.property || opts.claim?.dpp || {};
    const fields = buildLightspeedPrefillFields(opts);
    const url = buildLightspeedPrefillUrl({
        lightspeedUrl: opts.lightspeedUrl,
        loanType: opts.loanType,
        userId: opts.user?.id,
        utmContent: opts.utmContent,
        pathname: opts.pathname,
        property,
    });
    submitLightspeedPrefill({ url, fields });
}
